import React, { useEffect, useState } from 'react';
import {
    Drawer,
    Form,
    Input,
    Select,
    Button,
    Space,
    message,
    Typography,
    Checkbox,
    InputNumber,
    Card,
    Divider,
    Tooltip,
    Alert
} from 'antd';
import { UserOutlined, MailOutlined, LockOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { userService } from '../services/api';

const { Option } = Select;
const { Text } = Typography;

const PRIMARY_RED = '#DA291C';

const validateMessages = {
    required: '${label} es obligatorio',
    types: {
        email: '${label} no es un correo válido',
    },
};

const UserDrawer = ({ visible, onClose, mode, userData, onSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [roles, setRoles] = useState([]);
    const [rolesLoading, setRolesLoading] = useState(false);

    useEffect(() => {
        if (visible) {
            fetchRoles();
            if (mode === 'edit' && userData) {
                let pbxConfig = {};
                if (userData.pbx_user_config) {
                    try {
                        pbxConfig = typeof userData.pbx_user_config === 'string' 
                            ? JSON.parse(userData.pbx_user_config) 
                            : userData.pbx_user_config;
                        
                        // Convert time_limit (seconds) to h/m for UI
                        const totalSeconds = pbxConfig.time_limit || 0;
                        pbxConfig._hours = Math.floor(totalSeconds / 3600);
                        pbxConfig._minutes = Math.floor((totalSeconds % 3600) / 60);
                    } catch (e) {
                         console.error("Error parsing pbx_user_config", e);
                    }
                }

                form.setFieldsValue({
                    ...userData,
                    pbx_user_config: pbxConfig,
                    password: '' // Keep password empty on edit
                });
            } else {
                form.resetFields();
            }
        }
    }, [visible, mode, userData, form]);

    const fetchRoles = async () => {
        setRolesLoading(true);
        try {
            const res = await userService.getRoles();
            if (res.status === 'success') {
                setRoles(res.data);
            }
        } catch (error) {
            message.error('Error al cargar roles');
        } finally {
            setRolesLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            
            // Transform time_limit back to seconds
            if (values.pbx_user_config) {
                const { _hours = 0, _minutes = 0 } = values.pbx_user_config;
                values.pbx_user_config.time_limit = (_hours * 3600) + (_minutes * 60);
                
                // Remove temporary UI fields
                delete values.pbx_user_config._hours;
                delete values.pbx_user_config._minutes;
            }

            setLoading(true);

            let response;
            if (mode === 'create') {
                const payload = { ...values, sip_extension: '' };
                response = await userService.create(payload);
            } else {
                response = await userService.update({ ...values, uuid: userData.uuid });
            }

            if (response.status === 'success') {
                message.success(`Usuario ${mode === 'create' ? 'creado' : 'actualizado'} exitosamente`);
                onSuccess();
                onClose();
            }
        } catch (error) {
            if (error.errorFields) return;
            message.error(error.response?.data?.data?.message || 'Ocurrió un error en la operación');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Drawer
            title={mode === 'create' ? "Nuevo Usuario" : "Editar Usuario"}
            width={450}
            onClose={onClose}
            open={visible}
            extra={
                <Space>
                    <Button onClick={onClose}>Cancelar</Button>
                    <Button
                        type="primary"
                        onClick={handleSubmit}
                        loading={loading}
                        style={{ background: PRIMARY_RED, borderColor: PRIMARY_RED }}
                    >
                        Guardar
                    </Button>
                </Space>
            }
        >
            <Form
                form={form}
                layout="vertical"
                validateMessages={validateMessages}
                initialValues={{ 
                    state: 1,
                    pbx_user_config: {
                        manual_outbound_enabled: false,
                        international_enabled: false,
                        manual_callerid: '17410990',
                        time_limit: 0,
                        recording_enabled: true
                    }
                }}
            >
                {mode === 'create' && (
                    <Alert
                        message="Caso 'Reingreso de Personal'"
                        description="El reingreso de personal se gestiona como una nueva alta, lo que implica la asignación de una licencia nueva. No obstante, es posible utilizar el mismo nombre de usuario que tenía anteriormente."
                        type="info"
                        showIcon
                        style={{ marginBottom: 20 }}
                    />
                )}
                <Form.Item
                    label="Nombre Completo"
                    name="name"
                    rules={[{ required: true }]}
                >
                    <Input placeholder="Ej. Carlos Mendoza" prefix={<UserOutlined />} />
                </Form.Item>

                <Form.Item
                    label="Correo Electrónico"
                    name="email"
                    rules={[{ required: true, type: 'email' }]}
                >
                    <Input placeholder="carlos@example.com" prefix={<MailOutlined />} />
                </Form.Item>

                <Form.Item
                    label={
                        <span>
                            Nombre de Usuario&nbsp;
                            <Tooltip title="Puede reutilizar un nombre de usuario de un personal dado de baja. Se tratará como una licencia nueva.">
                                <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                            </Tooltip>
                        </span>
                    }
                    name="username"
                    rules={[{ required: true }]}
                >
                    <Input placeholder="cmendoza" prefix={<UserOutlined />} />
                </Form.Item>

                <Form.Item
                    label="Contraseña"
                    name="password"
                    rules={[{ required: mode === 'create' }]}
                    help={mode === 'edit' ? "Dejar en blanco para mantener la actual" : ""}
                >
                    <Input.Password placeholder="******" prefix={<LockOutlined />} />
                </Form.Item>


                <Form.Item
                    label="Rol"
                    name="role_id"
                    rules={[{ required: true }]}
                >
                    <Select placeholder="Seleccionar rol" loading={rolesLoading}>
                        {roles.map(role => (
                            <Option key={role.id} value={role.id}>
                                {role.name} - {role.portal === 'admin' ? 'administrador' : 'agente'}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Divider />

                <Card title="Configuración de Llamadas (PBX)" size="small" type="inner">
                     <Form.Item
                        name={['pbx_user_config', 'manual_outbound_enabled']}
                        valuePropName="checked"
                     >
                        <Checkbox>Habilitar Llamadas Manuales</Checkbox>
                     </Form.Item>

                     <Form.Item
                        noStyle
                        shouldUpdate={(prev, curr) => 
                            prev.pbx_user_config?.manual_outbound_enabled !== curr.pbx_user_config?.manual_outbound_enabled
                        }
                     >
                        {({ getFieldValue }) => {
                            const enabled = getFieldValue(['pbx_user_config', 'manual_outbound_enabled']);
                            return enabled ? (
                                <>
                                    <Form.Item
                                        label="CallerID Saliente"
                                        name={['pbx_user_config', 'manual_callerid']}
                                        rules={[{ required: true, message: 'CallerID es obligatorio' }]}
                                        help="Número que verán los destinatarios (DID)"
                                    >
                                        <Input placeholder="17410990" />
                                    </Form.Item>

                                    <Form.Item
                                        name={['pbx_user_config', 'international_enabled']}
                                        valuePropName="checked"
                                    >
                                        <Checkbox>Permitir Llamadas Internacionales</Checkbox>
                                    </Form.Item>

                                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                                        Límite de Tiempo (0 = Ilimitado)
                                    </Typography.Text>
                                    <Space align="start">
                                        <Form.Item
                                            name={['pbx_user_config', '_hours']}
                                            label="Hrs"
                                        >
                                            <InputNumber min={0} max={24} placeholder="0" />
                                        </Form.Item>
                                        <Form.Item
                                            name={['pbx_user_config', '_minutes']}
                                            label="Min"
                                        >
                                            <InputNumber min={0} max={59} placeholder="0" />
                                        </Form.Item>
                                    </Space>
                                    <Text type="secondary" style={{ display: 'block', fontSize: '11px', marginTop: -10, marginBottom: 15 }}>
                                        Duración máxima de la conversación
                                    </Text>

                                    <Form.Item
                                        name={['pbx_user_config', 'recording_enabled']}
                                        valuePropName="checked"
                                    >
                                        <Checkbox>Grabar Llamadas Salientes</Checkbox>
                                    </Form.Item>
                                </>
                            ) : null;
                        }}
                     </Form.Item>
                </Card>


            </Form>
        </Drawer>
    );
};

export default UserDrawer;
